/* Penoblo Federstärke-Dropdown mit Bild: Custom-Dropdown, das je Option das
   passende federstaerke-{slug}.png zeigt (native <select> kann keine Bilder).
   Steuert das versteckte Dawn-Varianten-Steuerelement -> Warenkorb/Preis bleiben
   unveraendert. Bild der gewaehlten Federstaerke erscheint im Trigger. */
class PnbNibSelect extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('.pnb-nib__trigger');
    this.menu = this.querySelector('.pnb-nib__menu');
    this.opts = [...this.querySelectorAll('.pnb-nib__opt')];
    this.trigimg = this.querySelector('.pnb-nib__trigimg');
    this.trigtext = this.querySelector('.pnb-nib__trigtext');
    this.values = this.opts.map((o) => (o.dataset.value || '').trim());
    this.root = this.closest('product-info') || this.closest('form') || document;
    if (!this.trigger || !this.menu) return;

    // Listener an ein AbortController-Signal binden -> disconnectedCallback raeumt
    // sie bei Section-Unload ab, inkl. der document-/root-Listener (sonst Leak +
    // Mehrfach-Fire, wenn this.root === document).
    const signal = (this._ac = new AbortController()).signal;

    this.trigger.addEventListener('click', (e) => { e.stopPropagation(); this.toggle(); }, { signal });
    this.opts.forEach((o) => o.addEventListener('click', () => this.choose((o.dataset.value || '').trim()), { signal }));
    document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); }, { signal });
    this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); }, { signal });
    // externe Aenderungen (z. B. Dawn-Re-Render) spiegeln
    this.root.addEventListener('change', () => this.syncFromNative(), { signal });

    // --- Tastatur: Custom-Dropdown als echte Listbox bedienbar (vorher war die
    //     Federstaerke nur per Maus waehlbar; natives select ist display:none). ---
    this.opts.forEach((o) => o.setAttribute('tabindex', '-1'));
    this.trigger.addEventListener('keydown', (e) => {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].indexOf(e.key) !== -1) { e.preventDefault(); this.openAndFocus(); }
    }, { signal });
    this.opts.forEach((o, i) => {
      o.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); (this.opts[i + 1] || this.opts[0]).focus(); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); (this.opts[i - 1] || this.opts[this.opts.length - 1]).focus(); }
        else if (e.key === 'Home') { e.preventDefault(); this.opts[0].focus(); }
        else if (e.key === 'End') { e.preventDefault(); this.opts[this.opts.length - 1].focus(); }
        else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.choose((o.dataset.value || '').trim()); this.trigger.focus(); }
        else if (e.key === 'Escape') { e.preventDefault(); this.close(); this.trigger.focus(); }
        else if (e.key === 'Tab') { this.close(); }
      }, { signal });
    });
  }

  openAndFocus() {
    this.open();
    const sel = this.opts.filter((o) => o.classList.contains('on'))[0] || this.opts[0];
    if (sel) sel.focus();
  }

  disconnectedCallback() {
    if (this._ac) this._ac.abort();
  }

  /* das native Steuerelement dieser Option finden.
     Bevorzugt EXAKT ueber den Options-Namen (data-option) -> verhindert Fehlgriff,
     wenn zwei Optionen denselben Wert teilen. Dawn: select name="options[<Name>]",
     radio name="<Name>-<Position>". Fallback: alte Heuristik ueber Optionswerte. */
  findNative() {
    const optName = (this.dataset.option || '').trim().toLowerCase();
    const selects = [...this.root.querySelectorAll('variant-selects select')];
    const radios = [...this.root.querySelectorAll('variant-selects input[type="radio"]')];

    if (optName) {
      const selByName = selects.find((s) => {
        const m = (s.name || '').match(/^options\[(.*)\]$/);
        return m && m[1].trim().toLowerCase() === optName;
      });
      if (selByName) return { type: 'select', el: selByName };

      const grpByName = radios.filter((r) => (r.name || '').trim().toLowerCase().indexOf(optName + '-') === 0);
      if (grpByName.length) return { type: 'radio', group: grpByName };
    }

    for (const s of selects) {
      const ovals = [...s.options].map((o) => (o.value || '').trim());
      if (this.values.some((v) => ovals.includes(v))) return { type: 'select', el: s };
    }
    const group = radios.filter((r) => this.values.includes((r.value || '').trim()));
    if (group.length) return { type: 'radio', group };
    return null;
  }

  toggle() { this.menu.hidden ? this.open() : this.close(); }
  open() { this.menu.hidden = false; this.trigger.setAttribute('aria-expanded', 'true'); }
  close() { this.menu.hidden = true; this.trigger.setAttribute('aria-expanded', 'false'); }

  choose(val) {
    if (!this.values.includes(val)) return;
    const native = this.findNative();
    if (native && native.type === 'select') {
      native.el.value = val;
      native.el.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (native && native.type === 'radio') {
      const r = native.group.find((x) => (x.value || '').trim() === val);
      if (r) { r.checked = true; r.dispatchEvent(new Event('change', { bubbles: true })); }
    }
    this.setSelected(val);
    this.close();
  }

  setSelected(val) {
    this.opts.forEach((o) => {
      const on = (o.dataset.value || '').trim() === val;
      o.classList.toggle('on', on);
      o.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    if (this.trigtext) this.trigtext.textContent = val;
    const opt = this.opts.find((o) => (o.dataset.value || '').trim() === val);
    const im = opt && opt.querySelector('.pnb-nib__optimg');
    if (im && this.trigimg) {
      this.trigimg.src = im.getAttribute('src');
      this.trigimg.style.display = im.style.display === 'none' ? 'none' : '';
    }
  }

  syncFromNative() {
    const native = this.findNative();
    let val = null;
    if (native && native.type === 'select') val = (native.el.value || '').trim();
    else if (native && native.type === 'radio') { const c = native.group.find((x) => x.checked); val = c && (c.value || '').trim(); }
    if (val && this.values.includes(val)) this.setSelected(val);
  }
}
if (!customElements.get('pnb-nib-select')) customElements.define('pnb-nib-select', PnbNibSelect);
